import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { Pin, Plus, RefreshCw } from 'lucide-react';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { Badge, Button, Card, Input, PageLoader, ScrollArea, Tabs, Typography } from '../components/symphony/ui';
import { ChatsEmpty } from '../components/symphony/chats/ChatsEmpty';
import { useActiveConversation } from '../components/chat/ActiveConversationContext';
import { ConversationRowMenu } from '../components/symphony/conversations/ConversationRowMenu';
import { DeleteConversationConfirm } from '../components/symphony/conversations/DeleteConversationConfirm';
import { RenameConversationDialog } from '../components/symphony/conversations/RenameConversationDialog';
import { SearchDialog } from '../components/symphony/sidebar/components/SearchDialog';
import { useRecipient } from '../components/settings/shared/settings-queries';
import { useConversationActions, type ConversationActionsFlow } from '../lib/use-conversation-actions';
import { useChats, type ChatListRow } from '../lib/chats';
import { chatsScrollKey, savedChatsScroll, saveChatsScroll } from '../lib/chats-cache';
import { MEETINGS_CHAT_CHANNEL, stringParam, useChatsFilters, visibleChatChannels } from '../lib/use-chats-filters';
import { useMeetingsAgentGate } from '../lib/meetings-agent-flag';
import { MeetingsChatsEmpty } from '../components/symphony/chats/MeetingsChatsEmpty';
import { useChatsLayout } from '../lib/use-chats-layout';
import { useChatsListWidth } from '../lib/use-chats-list-width';
import { ChatsListResizeHandle } from '../components/symphony/chats/ChatsListResizeHandle';
import { useOnboardingEntry } from '../lib/use-onboarding-entry';
import { useBasePath } from '../lib/use-base-path';
import { useTranslation } from '../lib/i18n';
import { usePageLoadBi } from '../lib/use-page-load-bi';
import { useGenericBi } from '../lib/use-generic-bi';
import { useLoadBi } from '../lib/use-load-bi';
import { messageDir } from '../lib/message-direction';
import { TURN_STALE_AFTER_MS, turnActive } from '../lib/turn-activity';
import { useConversations } from '../lib/conversations';
// Imported from the PURE module, not through the `lib/conversations` re-export: suites that mock
// `lib/conversations` wholesale (chats-page.test.ts) replace the module object, so a helper reached
// through it resolves to undefined and every row throws. The re-export is for call sites that already
// import from there; a page that only needs the helper should take it from its own module.
import { conversationDisplayTitle, isBriefConversation } from '../lib/conversation-display-title';
import styles from '../components/symphony/chats/Chats.module.css';
import { AgentsIslandBadge } from '../components/symphony/AgentsIslandBadge';
import { useNarrow } from '../lib/use-narrow';
import { useChatOverlayOpen } from '../lib/use-chat-overlay';

// The row PITCH — Figma 18693:74602 is an 86-tall card on a 24 gap. The list is virtualized on this
// and the keyboard navigation scrolls by it, so it must stay equal to the card height in
// Chats.module.css plus that gap.
const ROW_HEIGHT = 110;
// The phone frame's card (Figma [Mobile] New Chat 18693:74840): 84 tall + the 12 gap between cards —
// the same 84/12 both phones lay out. Must agree with `.mobile .rowButton` in Chats.module.css.
const MOBILE_ROW_HEIGHT = 96;
const MOBILE_CARD_HEIGHT = 84;
const OVERSCAN = 5;
// Two working chats must not pulse in lockstep; the design prototype stages its second row by 0.45s.
// Rows cycle through four offsets rather than growing one unboundedly with the index.
const WORKING_DOT_STAGGER_S = 0.45;
const WORKING_DOT_STAGGER_STEPS = 4;
const channelKeys: Record<string, string> = {
  WHATSAPP: 'sidebar.channelBadge.whatsapp', SMS: 'sidebar.channelBadge.sms', EXTERNAL_AGENT: 'sidebar.channelBadge.externalAgent',
  CALENDAR_MEETINGS: 'sidebar.channelBadge.meeting',
};
// A row says "Meeting"; the tab that lists them all says "Calendar Meetings".
const filterKeys: Record<string, string> = { ...channelKeys, '': 'chats.filter.all', CALENDAR_MEETINGS: 'chats.filter.calendarMeetings' };

const ChatRow = memo(function ChatRow({ row, selected, index, size, flow, onOpen, meetingsEnabled, mobile, pitch }: {
  row: ChatListRow; selected: boolean; index: number; size: number; flow: ConversationActionsFlow;
  onOpen: (id: string, messageId?: string) => void; meetingsEnabled: boolean; mobile: boolean; pitch: number;
}) {
  const { t } = useTranslation();
  // Name → start time → the "Untitled" word. A row with no name is common (the VMR namer skips
  // system-initiated and already-long threads), and three rows all reading "Untitled" is what QA
  // reported; the time tells them apart. One rule, shared with the rail and both phones.
  const title = conversationDisplayTitle(row.name, row.createdDate, t('common.untitled') || '');
  // The recap job reaches a chat only after a while; until then the row shows its last message, not nothing.
  const preview = row.matchedSnippet || row.recap || row.lastMessagePreview || '';
  const unreadLabel = [
    row.readState.hasUnreadMessages === true ? t('chats.unreadMessages') : null,
    row.readState.hasUnreadNotifications === true ? t('chats.unreadNotifications') : null,
  ].filter(Boolean).join(', ');
  const titleDir = useMemo(() => messageDir(title), [title]);
  const previewDir = useMemo(() => messageDir(preview), [preview]);
  const dateLabel = useMemo(() => {
    const date = row.updatedDate ? new Date(row.updatedDate) : null;
    return date && Number.isFinite(date.valueOf()) ? date.toLocaleDateString() : null;
  }, [row.updatedDate]);
  // The channel is now carried by the filter tabs, so the row no longer repeats it — WEB/APP/SMS and a
  // 1:1 WhatsApp thread lose their badge, as the design draws. Three exceptions survive:
  //   • a WhatsApp GROUP — "Whatsapp" covers the group and the 1:1 assistant thread alike;
  //   • a connected app's own NAME — "Connected app" names no app;
  //   • a MEETING, inside the meetings release — kept deliberately by the Calendar Meetings change
  //     (#10741) and pinned by its tests. The Figma frame predates that channel and cannot speak to
  //     it, so its owner's decision stands rather than this redesign's general rule.
  const badge = row.communicationChannel === 'CALENDAR_MEETINGS'
    ? (meetingsEnabled ? t(channelKeys.CALENDAR_MEETINGS!) : null)
    : row.communicationChannel === 'WHATSAPP' && row.isWaGroup ? t('sidebar.channelBadge.whatsappGroup')
      // A connected-app row with no platform name falls back to the GENERIC label rather than to
      // nothing: in the All view no tab is naming the channel, so this badge is the row's only
      // indicator that it is not an ordinary chat.
      : row.communicationChannel === 'EXTERNAL_AGENT'
        ? row.agentPlatform?.name ?? t('sidebar.channelBadge.externalAgent') : null;
  // A turn running in this chat right now. Read through `turnActive` so the 5-minute staleness bound
  // applies — a turn that died uncleanly leaves the stamp set, and a dot with no end is worse than none.
  //
  // The bound needs a CLOCK, not just a predicate: this row is memoized and its object does not change
  // when the stamp goes stale, so evaluating once at render would pulse past the expiry until something
  // else happened to re-render the list. `expiresAt` schedules exactly one re-render, at the moment the
  // stamp stops being believable, and only while a row is actually working — an idle list arms nothing.
  const [, setExpiryTick] = useState(0);
  const working = turnActive(row);
  useEffect(() => {
    if (!working) return undefined;
    const updatedMs = row.updatedDate ? Date.parse(row.updatedDate) : Number.NaN;
    if (!Number.isFinite(updatedMs)) return undefined;
    const remaining = updatedMs + TURN_STALE_AFTER_MS - Date.now();
    const timer = setTimeout(() => setExpiryTick((tick) => tick + 1), Math.max(0, remaining));
    return () => clearTimeout(timer);
  }, [working, row.updatedDate]);
  // No subtitle, badge or pin → a ONE-line card: the empty second line would still cost its gap and the
  // date/⋮ cluster is anchored to the first line, so both would sit above the card's centre (QA
  // "Content alignment on card"). Same rule as both phones' `ChatListRowView`.
  const singleLine = !preview && !badge && !row.pinned;
  // On the phone the row IS a card — the DS white Card on the stone page (Figma 18693:74840), 84 tall on
  // the 96 pitch. On desktop it stays the ghost row (the DS text Button IS Cards/Ghost, see .rowButton).
  const shell = {
    role: 'listitem', 'aria-posinset': index + 1, 'aria-setsize': size,
    className: `${styles.row} ${singleLine ? styles.singleLine : ''}`,
    style: { top: index * pitch, height: mobile ? MOBILE_CARD_HEIGHT : pitch },
  } as const;
  const dots = <>
    {working && <span className={styles.workingDot} role="img" aria-label={t('chats.working')}
      style={{ animationDelay: `${(index % WORKING_DOT_STAGGER_STEPS) * WORKING_DOT_STAGGER_S}s` }} />}
    {unreadLabel && <span className={styles.dot} role="img" aria-label={unreadLabel} />}
  </>;
  const content = <>
    <Button variant="text" fullWidth data-chat-index={index}
      className={`${styles.rowButton} ${selected ? styles.selected : ''}`}
      aria-current={selected ? 'true' : undefined} onClick={() => onOpen(row.id, row.messageId)}>
      <span className={styles.rowContent}>
        <span className={styles.rowHeading}>
          {/* The dot LEADS the title on the phone and trails it on desktop — both frames draw it that way
              deliberately (docs/chats-list-redesign.md §4), as both phones do. */}
          {mobile && dots}
          {/* One weight for every title (Figma Medium/Semi Bold 16/20 → `.sym-h3`; the phone frame's 14/18 →
              `.sym-h4`). Unread is carried by the dot beside it, as drawn — not by a heavier title, which the
              design no longer varies. */}
          <Typography component="span" variant={mobile ? 'h4' : 'h3'} noWrap dir={titleDir} className={styles.rowTitle}>{title}</Typography>
          {!mobile && dots}
        </span>
        {/* The badge rides the SUBTITLE line, not the title's — same as both phones. On the title line it
            competes with the title for a 360px split column and squeezes it to nothing. */}
        {!singleLine && <span className={styles.rowSubtitle}>
          {preview && <Typography component="span" variant="p2" color="secondary" noWrap dir={previewDir}>{preview}</Typography>}
          {badge && <Badge>{badge}</Badge>}
        </span>}
      </span>
    </Button>
    <div className={styles.rowMeta}>
      {dateLabel && <time dateTime={row.updatedDate}>{dateLabel}</time>}
      <ConversationRowMenu conv={row} title={title} flow={flow} />
    </div>
    {row.pinned && <Pin size={16} aria-hidden className={styles.pin} />}
  </>;
  return mobile ? <Card disablePadding {...shell}>{content}</Card> : <div {...shell}>{content}</div>;
});

export default function ChatsPage() {
  usePageLoadBi({ page_name: 'chats' });
  const router = useRouter();
  const basePath = useBasePath();
  const { t } = useTranslation();
  const { split } = useChatsLayout();
  const narrow = useNarrow();
  const chatOverlayOpen = useChatOverlayOpen();
  const chatsListWidth = useChatsListWidth();
  const { logPageClickBi } = useGenericBi();
  const { activeConversationId, setActiveConversationId, chatMode, setChatMode, openMaestroChat } = useActiveConversation();
  // The chat column is only THERE to be resized while it is docked beside the list. Its header can now
  // close it (×) and grow it to full width (⤢), and a handle for a column in neither state is a drag that
  // moves a boundary the reader cannot see. Mirrors AppFrame's `chatDocked` for the same two modes.
  const chatDockedBesideList = split && chatMode !== 'bubble' && chatMode !== 'fullscreen';
  // A row reads as selected only while its chat is actually SHOWING — the same rule AppFrame applies to
  // the rail (`sidebarSelectedId = chatOverlayOpen ? selectedConversationId : null`). The × can now close
  // the column while `activeConversationId` stays put (so re-opening the row restores it), and a
  // highlighted row with no chat beside it reads as a broken selection rather than a remembered one.
  const shownConversationId = chatMode === 'bubble' ? null : activeConversationId;
  const meetingsGate = useMeetingsAgentGate();
  const meetingsEnabled = meetingsGate.enabled;
  const { search, channel, setSearch, setChannel } = useChatsFilters(router, basePath, meetingsEnabled);
  // Until the gate answers, a meetings link reads as All. Nothing that latches — the first-row pick, the
  // empty-account handoff — may act on All's rows while the answer could still switch the tab. A yes is
  // not enough on its own: the filter picks it up one effect later, so wait for the tab itself.
  const awaitingMeetingsGate = stringParam(router.query.channel) === MEETINGS_CHAT_CHANNEL
    && (!meetingsGate.resolved || (meetingsEnabled && channel !== MEETINGS_CHAT_CHANNEL));
  const recipient = useRecipient();
  const owner = recipient.data?.email && recipient.data.metaSiteId ? JSON.stringify([recipient.data.email, recipient.data.metaSiteId]) : null;
  const onboarding = useOnboardingEntry();
  const combined = useChats(search, channel, router.isReady ? owner : null);
  const legacy = useConversations(combined.unsupported);
  const [legacySearchOpen, setLegacySearchOpen] = useState(false);
  useEffect(() => { if (combined.unsupported && search.trim()) setLegacySearchOpen(true); }, [combined.unsupported, search]);
  const legacyRows = useMemo<ChatListRow[]>(() => legacy.state.kind === 'ready' ? legacy.state.conversations
    .filter((row) => row.id && (!row.source || row.source === 'SCHEDULER') && (!channel || row.communicationChannel === channel))
    .map((row) => ({ ...row, id: row.id!, readState: { lastReadAt: null, lastReadCursor: null, hasUnreadMessages: null,
      hasUnreadNotifications: null, versions: { reader: null, messageHead: null, notifications: null } } })) : [], [legacy.state, channel]);
  const chats = combined.unsupported ? { ...combined, rows: legacyRows, isLoading: legacy.state.kind === 'loading',
    isError: legacy.state.kind === 'error', hasNextPage: false, isFetchingNextPage: false, debouncing: false,
    // "Empty" ignores briefs: the legacy list carries them (the sidebar shows them, as the phones' drawer
    // does), but this screen — like both phones' Chats screens — lists SCHEDULER rows only.
    serverReady: legacy.serverReady && legacy.state.kind === 'ready' && !legacy.state.hasMore
      && !legacy.state.conversations.some((row) => !isBriefConversation(row)),
    refetch: async () => { await legacy.refresh(); return combined.refetch(); } } : combined;
  useLoadBi('chats-list', { isLoading: chats.isLoading, isError: chats.isError });
  const scroller = useRef<HTMLElement>(null);
  const [viewport, setViewport] = useState({ top: 0, height: 800 });
  const scrollKey = chatsScrollKey(owner ?? '', search, channel);
  const restored = useRef<string | null>(null);
  const initialSelectionOwner = useRef<string | null>(null);
  const emptyEntryOwner = useRef<string | null>(null);
  const flow = useConversationActions({ page_name: 'chats', page_version: 'v1_chats' });
  const actions: ConversationActionsFlow = {
    ...flow,
    confirmDelete: async () => {
      const id = flow.target?.id;
      const ok = await flow.confirmDelete();
      // The shared flow already left the deleted chat and stripped it from the URL; here only the list's
      // first-row pick is re-armed, so a wide split lands on the next chat instead of an empty column.
      if (ok && id === activeConversationId) initialSelectionOwner.current = null;
      return ok;
    },
  };

  useEffect(() => {
    if (!router.isReady || (owner && emptyEntryOwner.current === owner)) return;
    const id = stringParam(router.query.conversation);
    if (id) setActiveConversationId(id);
    setChatMode(split || id || router.query.new === '1' ? 'expanded' : 'bubble');
  }, [router.isReady, router.query.conversation, router.query.new, split, owner, setActiveConversationId, setChatMode]);
  useEffect(() => {
    if (!router.isReady || !owner || initialSelectionOwner.current === owner || awaitingMeetingsGate) return;
    if (router.query.conversation || router.query.new === '1') { initialSelectionOwner.current = owner; return; }
    // Explicit links win. A wide initial visit reuses an accessible recent chat, then the newest row.
    // Filters and background refreshes never switch a chat the user has already opened.
    if (split && chats.rows.length) {
      initialSelectionOwner.current = owner;
      const next = chats.rows.find((row) => row.id === activeConversationId) ?? chats.rows[0]!;
      setActiveConversationId(next.id);
    }
  }, [router.isReady, router.query.conversation, router.query.new, owner, split, chats.rows, activeConversationId, setActiveConversationId,
    awaitingMeetingsGate]);

  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const update = () => {
      if (element.clientHeight) setViewport({ top: element.scrollTop, height: element.clientHeight });
    };
    const observer = new ResizeObserver(update);
    observer.observe(element);
    update();
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (restored.current === scrollKey || !chats.rows.length || !scroller.current) return;
    const element = scroller.current;
    element.scrollTop = savedChatsScroll(scrollKey);
    restored.current = scrollKey;
    setViewport({ top: element.scrollTop, height: element.clientHeight || 800 });
  }, [scrollKey, chats.rows.length]);

  // The list's pitch — the phone frame's cards on narrow, the desktop rows otherwise.
  const pitch = narrow ? MOBILE_ROW_HEIGHT : ROW_HEIGHT;
  const start = Math.max(0, Math.floor(viewport.top / pitch) - OVERSCAN);
  const end = Math.min(chats.rows.length, Math.ceil((viewport.top + viewport.height) / pitch) + OVERSCAN);
  useEffect(() => {
    if (end >= chats.rows.length - OVERSCAN && chats.hasNextPage && !chats.isFetching && !chats.isError && !chats.debouncing) {
      void chats.fetchNextPage();
    }
  }, [end, chats.rows.length, chats.hasNextPage, chats.isFetching, chats.isError, chats.debouncing, chats.fetchNextPage]);

  const open = useCallback((id: string, messageId?: string) => {
    setActiveConversationId(id);
    setChatMode('expanded');
    const query = { ...(search ? { q: search } : {}), ...(channel ? { channel } : {}),
      conversation: id, ...(messageId ? { message: messageId } : {}) };
    void router.push({ pathname: '/chats', query }, `${basePath}/chats?${new URLSearchParams(query)}`, { shallow: true, scroll: false });
  }, [setActiveConversationId, setChatMode, search, channel, router, basePath]);
  const newChat = () => {
    if (!onboarding.settled) return;
    logPageClickBi({ page_name: 'chats', button_name: 'new_chat' });
    // Always a NEW chat — never a resume of the newest one, unfinished onboarding or not (see useSidebarNav).
    // Only an account with no chat at all is sent to the assistant's greeting, which is its first chat.
    if (onboarding.route === 'kickoff' && !onboarding.hasConversations) {
      setChatMode('expanded');
      void router.push('/', `${basePath || '/'}`);
      return;
    }
    openMaestroChat();
    const query = { ...(search ? { q: search } : {}), ...(channel ? { channel } : {}), new: '1' };
    void router.push({ pathname: '/chats', query }, `${basePath}/chats?${new URLSearchParams(query)}`, { shallow: true, scroll: false });
  };
  const refreshFromStart = () => {
    // A deliberate refresh starts a new list at its newest row. Ordinary
    // navigation/invalidation preserves the retained pages and scroll position.
    saveChatsScroll(scrollKey, 0);
    restored.current = scrollKey;
    if (scroller.current) scroller.current.scrollTop = 0;
    setViewport(current => ({ ...current, top: 0 }));
    void combined.refreshFromStart();
  };
  useEffect(() => {
    // Only a successful unfiltered server result can establish that the user has
    // no chats. Cached placeholders, a failed refresh, and filtered empty pages
    // must never discard the list or replace an explicitly selected conversation.
    if (!router.isReady || !owner || !onboarding.resolved || emptyEntryOwner.current === owner || awaitingMeetingsGate
      || router.query.conversation || router.query.new === '1' || search.trim() || channel
      || !chats.serverReady || chats.isError || chats.debouncing || chats.hasNextPage || chats.rows.length) return;
    emptyEntryOwner.current = owner;
    if (onboarding.route === 'resume' && onboarding.resumeConversationId) { open(onboarding.resumeConversationId); return; }
    if (onboarding.route === 'kickoff') {
      setChatMode('fullscreen');
      void router.replace('/', basePath || '/');
      return;
    }
    openMaestroChat();
    setChatMode('fullscreen');
    // Replace the empty list entry so Back returns to the prior screen. Reuse
    // the app's existing full-screen new-chat route and its single chat owner.
    const query = { new: '1', chat: 'fullscreen' };
    void router.replace({ pathname: '/', query }, `${basePath}/?${new URLSearchParams(query)}`, { shallow: false });
  }, [router, owner, onboarding.resolved, onboarding.route, onboarding.resumeConversationId, search, channel,
    chats.serverReady, chats.isError, chats.debouncing, chats.hasNextPage, chats.rows.length, open, openMaestroChat, setChatMode, basePath,
    awaitingMeetingsGate]);
  const identityError = !owner && !recipient.isLoading && (recipient.isError || Boolean(recipient.data));
  const loading = !identityError && (!owner || chats.isLoading);

  return <GenericEventsRouteProvider value="chats">
    <section className={`${styles.page} ${split ? styles.split : ''} ${narrow ? styles.mobile : ''}`} aria-label={t('chats.title')}>
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <Typography variant="display" component="h1">{t('chats.title')}</Typography>
          {/* On a phone the island rides the top bar (AppFrame) and New Chat is docked at the foot. */}
          {!narrow && <div className={styles.titleActions}>
            {/* The agents island rides this row rather than AppFrame's floating top-right pin, which sat
                over New Chat in the same corner. Hidden while an open chat covers the list — ChatHeader
                draws its own there. Mirrors AppFrame's `pillOnChatsList`. */}
            {!(chatOverlayOpen && (narrow || chatMode === 'fullscreen')) && (
              <GenericEventsRouteProvider value="agents-island">
                <AgentsIslandBadge />
              </GenericEventsRouteProvider>
            )}
            {/* Outlined and label-only — the same New Chat the expanded chat's header shows (Figma 20312-113774). */}
            <Button variant="outlined" disabled={!onboarding.settled} onClick={newChat}>{t('chats.newChat')}</Button>
          </div>}
        </div>
        <div className={styles.search}>
          {combined.unsupported ? <Button variant="outlined" onClick={() => setLegacySearchOpen(true)}>{t('sidebar.search.placeholderSymphony')}</Button> : <Input kind="search" value={search} onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')} placeholder={t('sidebar.search.placeholderSymphony')}
            aria-label={t('sidebar.search.placeholderSymphony')} maxLength={200} />}
        </div>
        {/* The DS tab strip, not Buttons: these say WHICH VIEW you are looking at, which is what a tab
            is for and what Figma draws (`.Symphony Tabs`, Size=S → 30). */}
        <ScrollArea axis="horizontal" className={styles.filters} aria-label={t('chats.title')}>
          <Tabs size="small" variant={narrow ? 'underline' : 'pill'} label={t('chats.title')} value={channel} onChange={setChannel}
            items={visibleChatChannels(meetingsEnabled).map((value) => ({ value, label: t(filterKeys[value]!) }))} />
        </ScrollArea>
        {!combined.unsupported && combined.automaticRefreshPaused && <Button variant="outlined" disabled={chats.isFetching}
          startIcon={<RefreshCw size={16} aria-hidden />} onClick={refreshFromStart}>{t('common.refresh')}</Button>}
      </header>
      <ScrollArea ref={scroller} className={styles.list} onScroll={(event) => {
        const element = event.currentTarget;
        setViewport({ top: element.scrollTop, height: element.clientHeight });
        if (restored.current === scrollKey) saveChatsScroll(scrollKey, element.scrollTop);
      }} onKeyDown={(event) => {
        const target = event.target as HTMLElement;
        const current = target.closest<HTMLElement>('[data-chat-index]');
        if (!current || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const index = Number(current.dataset.chatIndex);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? chats.rows.length - 1
          : Math.max(0, Math.min(chats.rows.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
        event.preventDefault();
        const element = event.currentTarget;
        const top = next * pitch;
        if (top < element.scrollTop || top + pitch > element.scrollTop + element.clientHeight) element.scrollTop = top;
        setViewport({ top: element.scrollTop, height: element.clientHeight });
        requestAnimationFrame(() => element.querySelector<HTMLElement>(`[data-chat-index="${next}"]`)?.focus());
      }} aria-busy={loading || chats.debouncing}>
        {/* Figma "Search Loading" (18693:74744): the branded loop centred in the list area, not a
            line of text. `PageLoader`'s LottieLoader carries its own role="status" and label. */}
        {loading && <PageLoader size={64} />}
        {(identityError || chats.isError) && <div role="alert" className={styles.placeholder}>
          <Typography>{t('chats.loadFailed')}</Typography>
          <Button variant="outlined" onClick={() => {
            if (!owner) void recipient.refetch();
            else if (combined.automaticRefreshPaused && !combined.unsupported) refreshFromStart();
            else void chats.refetch();
          }}>{t('common.retry')}</Button>
        </div>}
        {!loading && !identityError && !chats.isError && !chats.rows.length
          && (channel === MEETINGS_CHAT_CHANNEL && !search && !chats.debouncing
            ? <MeetingsChatsEmpty />
            : <ChatsEmpty label={t(search || chats.debouncing ? 'search.noResults' : 'chats.empty')} />)}
        <div role="list" style={{ height: chats.rows.length * pitch, position: 'relative' }}>
          {chats.rows.slice(start, end).map((row, index) => <ChatRow key={`${row.id}:${row.messageId ?? ''}`} row={row}
            selected={row.id === shownConversationId} index={start + index} size={chats.hasNextPage ? -1 : chats.rows.length}
            flow={actions} onOpen={open} meetingsEnabled={meetingsEnabled} mobile={narrow} pitch={pitch} />)}
        </div>
        {chats.isFetchingNextPage && <div role="status" className={styles.placeholder}>{t('common.loading')}</div>}
      </ScrollArea>
      {/* New Chat is DOCKED at the foot on the phone (Figma 18693:74888), not in the header as on desktop:
          the header already carries the back chevron and the agents island, and a hug-width CTA up there is
          the hardest reach on a tall screen. Same as both phones. */}
      {narrow && <div className={styles.dockedNewChat}>
        <Button size="large" fullWidth disabled={!onboarding.settled} onClick={newChat}
          startIcon={<Plus size={16} aria-hidden />}>{t('chats.newChat')}</Button>
      </div>}
      {/* Split only — `stacked` shows one of the list/conversation at a time, so there is nothing
          to divide. Spans the whole page (not just the list ScrollArea) so it reaches the header
          too, matching FloatingChat's own dock handle, which spans its whole panel height. */}
      {chatDockedBesideList && (
        <ChatsListResizeHandle width={chatsListWidth} label={t('chats.listResizeHandle')}
          onDraggingChange={() => {}} />
      )}
    </section>
    <RenameConversationDialog flow={actions} />
    <DeleteConversationConfirm flow={actions} />
    {legacySearchOpen && <SearchDialog initialQuery={search} state={legacy.state} selectedId={activeConversationId} onClose={() => setLegacySearchOpen(false)} />}
  </GenericEventsRouteProvider>;
}
